/* Plyconcept — motion.js
   Everything that responds to the scroll position. No dependencies, one rAF loop,
   and it writes only CSS custom properties and classes so the compositor does the work.

   Three jobs:
     1. [data-reveal]  — fade and rise into view once, via IntersectionObserver.
     2. [data-hero-media] / [data-parallax] — scroll-linked transforms.
     3. [data-chrome]  — the header floats over the home hero, then re-enters as a
        fixed bar once the hero has gone.

   prefers-reduced-motion is honoured at the source: snippets/motion-boot.liquid never
   adds html.motion for those visitors, so nothing is ever hidden, and this file then
   reveals everything and never attaches a scroll listener.

   window.__plyMotion is the liveness flag the boot snippet watches. If this file fails
   to load, the class is withdrawn after 2.5s and the page is simply static. */
(function () {
  'use strict';
  window.__plyMotion = true;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. reveals ---------- */
  function reveals() {
    var els = document.querySelectorAll('[data-reveal]:not(.in)');
    if (!els.length) return;
    if (REDUCED || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(els, function (e) { e.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('in');
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.06 });
    Array.prototype.forEach.call(els, function (e) { io.observe(e); });
  }

  /* ---------- 2. scroll-linked ---------- */
  var items = [];
  var chrome = null;
  var heroH = 0;
  var vh = window.innerHeight;
  var ticking = false;

  function measure() {
    vh = window.innerHeight;
    items = Array.prototype.map.call(
      document.querySelectorAll('[data-hero-media],[data-parallax]'),
      function (el) {
        var r = el.getBoundingClientRect();
        return {
          el: el,
          top: r.top + window.pageYOffset,
          h: r.height || 1,
          k: parseFloat(el.getAttribute('data-parallax') || '0'),
          hero: el.hasAttribute('data-hero-media')
        };
      }
    );
    var hero = document.querySelector('[data-hero]');
    heroH = hero ? hero.getBoundingClientRect().height : 0;
  }

  function clamp(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  function frame() {
    ticking = false;
    var y = window.pageYOffset;

    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it.hero) {
        it.el.style.setProperty('--hp', clamp(y / Math.max(1, it.h)).toFixed(4));
      } else {
        var p = clamp((y + vh - it.top) / (vh + it.h));   // 0..1 crossing the viewport
        it.el.style.setProperty('--py', ((p - 0.5) * 2 * it.k * 100).toFixed(2) + 'px');
      }
    }

    if (chrome && heroH > 0) {
      // 60px of hysteresis either side so it cannot flicker at the boundary.
      chrome.classList.toggle('is-fixed', y > heroH - 60);
      chrome.classList.toggle('is-lifted', y > heroH + 40);
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(frame);
  }

  function start() {
    chrome = document.querySelector('[data-chrome]');
    reveals();
    if (REDUCED) {
      if (chrome && document.querySelector('[data-hero]')) chrome.classList.add('is-fixed', 'is-lifted');
      return;
    }
    measure();
    frame();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { measure(); frame(); }, { passive: true });
    // Images settling changes every offset just measured.
    window.addEventListener('load', function () { measure(); frame(); });
  }

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
